import rootRender from '@react/utils/root-render';
import { getDataFromPage } from '@react/utils/';
import { clsx } from 'clsx';
import throttle from 'lodash.throttle';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import animateRule from '@/js/animation/rule';
import animateFadeIn from '@/js/animation/fade-in';
import { useIsBreakpoint } from '../utils/hooks';
import { CUSTOM_EVENTS } from '@/js/lib/constants';

gsap.registerPlugin(ScrollTrigger);

type Link = {
  id: string;
  name: string;
  el?: HTMLElement;
};

type PageData = {
  title: string;
  anchorList: { [key: string]: string };
};

const Menu = ({
  activeItem,
  isOpen,
  onClick,
}: {
  activeItem: string;
  isOpen: boolean;
  onClick: () => void;
}) => {
  return (
    <button
      type="button"
      className="anchor-links__menu"
      onClick={onClick}
      aria-controls="anchors-dropdown"
      aria-expanded={isOpen}
    >
      <span>{activeItem}</span>
      <i className="icon-chevron-down" />
    </button>
  );
};

const Links = ({
  items,
  onClick = null,
}: {
  items: Link[];
  onClick: (el: Link['el']) => void;
}) => (
  <ul
    className={clsx('anchor-links__list', {
      'anchor-links__list--grid': items.length >= 6,
    })}
    style={
      {
        '--cols': Math.min(Math.ceil(items.length / 2), 5),
      } as React.CSSProperties
    }
  >
    {items.map(item => (
      <li className="anchor-links__list-item" key={`anchor-${item.id}`}>
        <button onClick={() => onClick(item.el)} type="button">
          <span>{item.name}</span>
        </button>
      </li>
    ))}
  </ul>
);

const ClonedCta = () => {
  const refCta = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const originalCta = document.querySelector('.anchor-links__cta');
    if (originalCta && refCta.current) {
      const clonedCta = originalCta.cloneNode(true) as HTMLElement;

      refCta.current.appendChild(clonedCta);

      originalCta.remove();
    }
  }, []);

  return <div ref={refCta} className="anchor-links__cloned-cta-container" />;
};

function AnchorLinks() {
  const ref = useRef<HTMLDivElement>(null);
  const refInner = useRef<HTMLDivElement>(null);
  const refRule = useRef<HTMLHRElement>(null);
  const refDropdown = useRef<HTMLDivElement>(null);

  const [isStuck, setIsStuck] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [activeItem, setActiveItem] = useState<undefined | string>(undefined);

  const data: PageData = getDataFromPage('react-anchor-links-data');
  const { title, anchorList } = data;
  const isMd = useIsBreakpoint('md');
  const isXl = useIsBreakpoint('xl');
  const offsetDelta = 100;
  const isSidePanel = !!document.querySelector('.side-panel');
  const anchorItems: Link[] = Object.entries(anchorList).map(([key, val]) => ({
    id: key,
    name: val,
    el: document.getElementById(key),
  }));

  useEffect(() => {
    // Initial animations
    animateFadeIn(ref.current);
    animateRule(refRule.current);
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleScroll = useCallback(
    throttle(() => {
      let active = anchorItems[0];

      for (const item of anchorItems) {
        if (!item.el) continue;

        const offsetTop =
          getOffset(item.el) - document.body.clientTop - offsetDelta - 10;

        if (scrollY > offsetTop) {
          active = item;
        }
      }
      setActiveItem(active.name);
    }, 250),
    [anchorItems]
  );

  const getOffset = (el: Link['el']) => {
    const { top } = el.getBoundingClientRect();
    const { scrollY } = window;

    return top + scrollY;
  };

  const scrollTo = (target: Link['el']) => {
    const offsetPosition = getOffset(target) - offsetDelta;
    const scrollEvent = new CustomEvent(CUSTOM_EVENTS.scrolledTo, {
      detail: target,
    });

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
    setIsOpen(false);
    target.dispatchEvent(scrollEvent);
  };

  const unsetParent = () => {
    const parent = ref.current.parentElement.parentElement.closest<HTMLElement>(
      '[style*="transform"]'
    );

    if (parent) parent.style.transform = null;
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    if (!isStuck) {
      setIsOpen(false);
    }
  }, [isStuck]);

  useEffect(() => {
    const articleSideElement = document.querySelector<HTMLElement>(
      '.article .article__side'
    );

    if (articleSideElement) {
      articleSideElement.style.zIndex = isOpen ? '20' : '';
    }

    if (!isOpen) return;

    refDropdown.current.style.maxHeight = `calc(100vh - ${refInner.current.offsetHeight}px)`;
  }, [isOpen]);

  useLayoutEffect(() => {
    const onEnter = () => {
      ref.current.style.height = 'auto';
      unsetParent();
      setIsStuck(true);
    };

    const onUpdate = () => {
      if (isSidePanel && isXl) {
        const contentWidth = document.getElementById('main').offsetWidth;
        ref.current.style.width = `${contentWidth}px`;
        ref.current.style.maxWidth = 'none';
      }
    };

    const onLeaveBack = () => {
      setIsStuck(false);
      ref.current.style.width = null;
    };

    ScrollTrigger.create({
      trigger: ref.current,
      start: 'top top',
      end: '99999',
      pin: true,
      pinSpacing: false,
      onEnter,
      onUpdate,
      onLeaveBack,
    });

    animateFadeIn(refInner.current);
    animateRule(refRule.current);

    setTimeout(() => {
      unsetParent();
    }, 1400);
  }, [isSidePanel, isXl]);

  return (
    <div
      className={clsx('anchor-links', {
        stuck: isStuck,
        open: isOpen,
      })}
      ref={ref}
    >
      <div
        ref={refInner}
        className="anchor-links__inner animate-fade-in js-animate-fade-in"
      >
        <div className="anchor-links__title-inner">
          <div className="anchor-links__title">{title}</div>
          {(!isMd || isStuck) && (
            <Menu
              activeItem={activeItem || anchorItems[0].name}
              isOpen={isOpen}
              onClick={() => setIsOpen(!isOpen)}
            />
          )}
        </div>
        <div className="anchor-links__list-wrapper">
          {isMd && !isStuck && <Links items={anchorItems} onClick={scrollTo} />}
          <ClonedCta />
        </div>
      </div>

      <hr
        ref={refRule}
        className="anchor-links__rule rule rule--full-width animate-rule js-animate-rule"
      />

      {isOpen ? (
        <div
          className="anchor-links__dropdown"
          id="anchors-dropdown"
          ref={refDropdown}
        >
          <Links items={anchorItems} onClick={scrollTo} />
        </div>
      ) : null}
    </div>
  );
}

rootRender('react-anchor-links', <AnchorLinks />);
